import { Navigate, createBrowserRouter } from 'react-router';
import { RequireSession } from '../auth/RequireSession';
import { ContainerLayout } from '../features/containers/ContainerLayout';
import { LegacyKey, LegacySection } from '../features/containers/LegacyRedirect';

/**
 * React Router 8's `lazy` resolves to route PROPERTIES, not to a component: returning the
 * module itself gives a route with no Component and a blank screen with no error.
 */
const route = (load: () => Promise<{ default: React.ComponentType }>) => async () => ({
  Component: (await load()).default,
});

export const router = createBrowserRouter([
  { path: '/', lazy: route(() => import('../routes/Landing')) },
  { path: '/callback', lazy: route(() => import('../routes/Callback')) },
  {
    // RequireSession renders AppShell, which owns the <Outlet/> these children land in.
    // Everything below this line has a session and a tenant, so no screen has to ask.
    Component: RequireSession,
    children: [
      {
        // Every page about a container's contents. ContainerLayout checks the container is
        // the tenant's and remounts these when it changes.
        path: 'containers/:containerId',
        Component: ContainerLayout,
        children: [
          { index: true, element: <Navigate to="overview" replace /> },
          { path: 'overview', lazy: route(() => import('../routes/Overview')) },
          { path: 'keys', lazy: route(() => import('../routes/KeysList')) },
          {
            path: 'keys/:keyId',
            lazy: route(() => import('../routes/KeyDetail')),
            children: [
              { index: true, lazy: route(() => import('../routes/KeyOverviewTab')) },
              { path: 'versions', lazy: route(() => import('../routes/KeyVersionsTab')) },
              { path: 'activity', lazy: route(() => import('../routes/KeyActivityTab')) },
            ],
          },
          { path: 'api-tokens', lazy: route(() => import('../routes/ApiTokens')) },
          // The page's name until API keys were renamed API tokens, kept for bookmarks:
          // the same page of the container the URL names.
          { path: 'api-keys', element: <LegacySection section="api-tokens" /> },
          { path: 'firewalls', lazy: route(() => import('../routes/Firewalls')) },
          { path: 'usage', lazy: route(() => import('../routes/Usage')) },
        ],
      },
      { path: 'containers', lazy: route(() => import('../routes/Containers')) },
      // The flat paths from before containers: sign-in's returnTo, the landing page and
      // every old bookmark. Each goes to the same page of the current container.
      { path: 'overview', element: <LegacySection section="overview" /> },
      { path: 'keys', element: <LegacySection section="keys" /> },
      { path: 'keys/:keyId/*', Component: LegacyKey },
      { path: 'api-tokens', element: <LegacySection section="api-tokens" /> },
      { path: 'api-keys', element: <LegacySection section="api-tokens" /> },
      { path: 'firewalls', element: <LegacySection section="firewalls" /> },
      { path: 'usage', element: <LegacySection section="usage" /> },
      { path: 'billing', lazy: route(() => import('../routes/Billing')) },
      { path: 'settings', lazy: route(() => import('../routes/Settings')) },
      { path: '*', lazy: route(() => import('../routes/NotFound')) },
    ],
  },
]);
